import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import TimeRangeSelector from '../components/TimeRangeSelector';
import RMSChart from '../components/RMSChart';
import GaugeChart from '../components/GaugeChart';
import MiniLineChart from '../components/MiniLineChart';
import TempIcon from '../assets/icons/temp.svg';
import BatteryIcon from '../assets/icons/battery.svg';

function UnitView() {
  const location = useLocation();
  const [units, setUnits] = useState([]);
  const [range, setRange] = useState('Live');
  const [unit, setUnit] = useState(location.state?.unit || '');
  const [data, setData] = useState([]);
  const [fails, setFails] = useState({ x: 0, y: 0, z: 0 });
  const [showLimits, setShowLimits] = useState(true);
  const user =
    localStorage.getItem('username') ||
    sessionStorage.getItem('username') ||
    'demo';

  useEffect(() => {
    fetch(`/get_units.php?username=${encodeURIComponent(user)}`)
      .then((res) => res.json())
      .then((d) => setUnits(d))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!units.length) return;
    if (location.state?.unit && units.includes(location.state.unit)) {
      setUnit(location.state.unit);
    } else if (!unit) {
      setUnit(units[0]);
    }
  }, [units, location.state, unit]);

  useEffect(() => {
    if (!unit) return;
    fetch(
      `/get_unit_data.php?table=${encodeURIComponent(unit)}&range=${encodeURIComponent(range)}&full=1&username=${encodeURIComponent(
        user
      )}`
    )
      .then((res) => res.json())
      .then((d) => setData(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [unit, range, user]);

  useEffect(() => {
    if (!unit) return;
    fetch(`/get_user_devices.php?username=${encodeURIComponent(user)}`)
      .then((res) => res.json())
      .then((list) => {
        const dev = Array.isArray(list) ? list.find((d) => d.unit === unit) : null;
        if (dev) {
          setFails({
            x: parseFloat(dev.fail_x) || 0,
            y: parseFloat(dev.fail_y) || 0,
            z: parseFloat(dev.fail_z) || 0,
          });
        }
      })
      .catch(() => {});
  }, [unit, user]);

  const temps = data.map((d) => parseFloat(d.temp)).filter((v) => !isNaN(v));
  const tempStats = temps.length
    ? {
        high: Math.max(...temps),
        low: Math.min(...temps),
        avg: temps.reduce((a, b) => a + b, 0) / temps.length,
      }
    : { high: 0, low: 0, avg: 0 };

  const labels = data.map((d) => d.inserted_at);
  const tempValues = data.map((d) => parseFloat(d.temp)).filter((v) => !isNaN(v));
  const batteryValues = data.map((d) => parseFloat(d.battery_volt)).filter((v) => !isNaN(v));

  const rmsX = data.map((d) => parseFloat(d.rms_x)).filter((v) => !isNaN(v));
  const rmsY = data.map((d) => parseFloat(d.rms_y)).filter((v) => !isNaN(v));
  const rmsZ = data.map((d) => parseFloat(d.rms_z)).filter((v) => !isNaN(v));

  const avgRms = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
  const batteryLast = batteryValues.length > 0 ? batteryValues[batteryValues.length - 1] : 0;

  const panelClass = useMemo(
    () =>
      'relative overflow-hidden rounded-[2rem] border border-white/5 bg-[#10131a]/80 p-5 text-white shadow-xl shadow-black/40 backdrop-blur-xl',
    []
  );

  return (
    <div className="space-y-6 text-white">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <h1 className="text-3xl font-semibold tracking-tight">Unit View</h1>
        <div className="w-full lg:ml-auto lg:max-w-xl">
          <TimeRangeSelector
            units={units}
            onRangeChange={setRange}
            onUnitChange={setUnit}
            selectedUnit={unit}
            groupId="unit-view-range"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[4fr_1fr]">
        <div className="flex flex-col gap-6">
          <div className={`${panelClass} xl:h-[30rem]`}>
            <button
              onClick={() => setShowLimits((s) => !s)}
              className="absolute left-6 top-6 z-10 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/20"
            >
              {showLimits ? 'Hide Limit' : 'Show Limit'}
            </button>
            <div className="flex h-full flex-col gap-4">
              <div className="pt-4 text-center text-lg font-semibold tracking-wide text-white/80">RMS X, Y, Z</div>
              <div className="relative flex-1 overflow-hidden rounded-[1.5rem] p-2">
                <RMSChart data={data} range={range} limits={fails} showLimits={showLimits} />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className={`${panelClass} flex flex-col gap-3 py-4`}>
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold uppercase tracking-[0.3em] text-white/50">Temperature</h3>
                <span className="rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs text-white/70">
                  High {tempStats.high.toFixed(1)}°
                </span>
              </div>
              <div className="flex items-center gap-3">
                <img src={TempIcon} alt="Temp" className="h-12 w-12" />
                <div className="flex-1 overflow-hidden">
                  <MiniLineChart
                    labels={labels}
                    data={tempValues}
                    label="Temp"
                    color="#FFD700"
                    format={(v) => parseInt(v)}
                  />
                </div>
              </div>
            </div>

            <div className={`${panelClass} flex flex-col gap-3 py-4`}>
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold uppercase tracking-[0.3em] text-white/50">Battery Voltage</h3>
                <span className="rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs text-white/70">
                  Last {batteryLast.toFixed(1)}v
                </span>
              </div>
              <div className="flex items-center gap-3">
                <img src={BatteryIcon} alt="Battery" className="h-12 w-12" />
                <div className="flex-1 overflow-hidden">
                  <MiniLineChart
                    labels={labels}
                    data={batteryValues}
                    label="Battery"
                    color="#00FF7F"
                    format={(v) => parseFloat(v).toFixed(1)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-5">
          <div className={`${panelClass} flex items-center justify-center p-6`}>
            <GaugeChart value={avgRms(rmsX)} max={fails.x} label="Average X" />
          </div>
          <div className={`${panelClass} flex items-center justify-center p-6`}>
            <GaugeChart value={avgRms(rmsY)} max={fails.y} label="Average Y" />
          </div>
          <div className={`${panelClass} flex items-center justify-center p-6`}>
            <GaugeChart value={avgRms(rmsZ)} max={fails.z} label="Average Z" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default UnitView;
